/**
 * "View server as role" (Discord's preview; user-management §4.2): the
 * owner or an Administrator picks one or more roles in Server settings and
 * the client shows the server as a member holding exactly those roles (and
 * not the owner) would see it: channels, composer, voice, tools. A preview
 * only; the server keeps checking every action with the real roles. A bar
 * at the top says so and ends it (or Escape, when nothing else is open). The
 * bar is drawn in the overlay layer, above the title bar and every panel, so
 * a mouse always reaches its buttons.
 */
import type { Snowflake } from '@neosocial/protocol';
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useT } from '../i18n';
import { useData } from '../store/data';
import { updateData } from '../store/data';
import { useActiveGuildId } from '../store/selectors';
import { openAdmin, useUi } from '../store/ui';
import { Btn, Icon } from '../ui/kit';
import { overlayOpen, portalRoot } from '../ui/overlay';

/** Starts the preview with these roles (@everyone alone: an empty list). */
export function startViewAs(guildId: Snowflake, roleIds: Snowflake[]): void {
  updateData((s) => ({ ...s, viewAs: { guildId, roleIds: [...new Set(roleIds)] } }));
  useUi.getState().set({ admin: null });
}

export function exitViewAs(): void {
  updateData((s) => (s.viewAs ? { ...s, viewAs: null } : s));
}

export function ViewAsBar() {
  const { t } = useT();
  const gid = useActiveGuildId();
  const names = useData((d) => {
    if (!d.viewAs || d.viewAs.guildId !== gid) return null;
    const g = gid ? d.guilds[gid] : undefined;
    const list = d.viewAs.roleIds
      .map((id) => g?.roles.find((r) => r.id === id)?.name)
      .filter((x): x is string => !!x);
    return list.length ? list.join(', ') : '@everyone';
  });
  const on = names !== null;
  useEffect(() => {
    if (!on) return;
    // Escape ends the preview; a menu or dialog that is open takes it first.
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented || overlayOpen()) return;
      exitViewAs();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [on]);
  if (names === null) return null;
  return createPortal(
    <div className="ns-viewas" role="status">
      <Icon name="eye" />
      <span dir="auto">{t('roles.viewAs.bar', { roles: names })}</span>
      <Btn
        size="xs"
        kind="ghost"
        onClick={() => {
          exitViewAs();
          openAdmin({ tab: 'roles' });
        }}
      >
        {t('roles.viewAs.back')}
      </Btn>
      <Btn size="xs" kind="primary" onClick={exitViewAs}>
        {t('roles.viewAs.exit')}
      </Btn>
    </div>,
    portalRoot(),
  );
}
